import {
    CreateExtArgs,
    CreateExtensionArgument,
    DalValue,
    Extension,
    ExtensionAPI,
    pointerUtils,
    InitializeExtArgs,
    Namespace
} from '@wix/document-manager-core'
import _ from 'lodash'
import {ReportableError} from '@wix/document-manager-utils'
import {displayedOnlyStructureUtil} from '@wix/santa-core-utils'
import {extractBaseComponentId, createInflatedRefId} from '../utils/refStructureUtils'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import type {PossibleViewModes, Pointer, CompRef, IConnectionItem} from '@wix/document-services-types'
import type {ConnectionsAPI} from './connections'
import {
    MASTER_PAGE_ID,
    NICKNAMES,
    COMP_TYPES,
    VIEWER_DATA_TYPES,
    VIEW_MODES,
    COMPS_DATA_TYPE,
    DATA_TYPES
} from '../constants/constants'
import type {ComponentsMetadataAPI} from './componentsMetadata/componentsMetadata'
import type {DalPointers} from '../types'
import type {WixCodeExtensionAPI} from './wixCode'
import {getComponentNicknameByType} from './componentsMetadata/utils/nicknameUtils'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {RefOverridesExtensionAPI} from './refOverrides'
import {getNicknameFromConnectionList, getWixCodeConnectionItem, findConnectionByContext} from '../utils/nicknamesUtils'
import type {NicknameContextExtensionAPI} from './nicknameContext'
import type {PageAPI, PageExtensionAPI} from './page'

const {getRepeaterTemplateId} = displayedOnlyStructureUtil
const {getPointer} = pointerUtils
const {VALIDATIONS} = NICKNAMES
const MAX_NICKNAME_LENGTH = 128
type HasComponentWithThatNicknameFn = (
    dalPointers: DalPointers,
    containingPagePointer: Pointer,
    searchedNickname: string,
    compPointerToExclude: Pointer | undefined
) => boolean
export type SetNickname = (compPointer: CompRef, nickname: string, context?: CompRef | null) => void
export type GetNextSuffixIndex = (compNickname: string, compsNicknames: UsedNickNames) => number
export type ValidateNickname = (
    compPointer: Pointer,
    nickname: string,
    hasComponentWithThatNicknameFn?: HasComponentWithThatNicknameFn
) => void
export type GenerateNicknamesForComponent = (
    compPointer: CompRef,
    pagePointer: Pointer,
    viewMode?: PossibleViewModes
) => void

export type GenerateConnectionOverrideForRefComponentRoot = (
    refCompPointer: Pointer,
    pagePointer: Pointer,
    rootCompId: string,
    rootComponentType: string
) => void

const ALL_PAGES_FILTER = 'all'

export type ShouldSetNickname = (compPointer: CompRef, context?: CompRef | null) => boolean
type UsedNickNames = {[x: string]: string} | undefined
type GetComponentNickname = (compPointer: Pointer, context?: CompRef | null) => Record<string, string>
export interface NicknamesAPI {
    hasComponentWithThatNickname(
        containingPagePointer: Pointer,
        searchedNickname: string,
        compPointerToExclude?: Pointer | undefined
    ): boolean
    setNickname: SetNickname
    getNextSuffixIndex: GetNextSuffixIndex
    generateNicknamesForComponent: GenerateNicknamesForComponent
    shouldSetNickname: ShouldSetNickname
    validateNickname: ValidateNickname
    getComponentNickname: GetComponentNickname
    generateConnectionOverrideForRefComponentRoot: GenerateConnectionOverrideForRefComponentRoot
    setNicknamesForComponentsWithoutNickname(
        comps: CompRef[],
        defaultCompNickname: string,
        usedNickNames: UsedNickNames,
        context: CompRef | null,
        pagePointer: CompRef
    ): CompRef[]
    getNicknames(comps: Pointer[], context?: CompRef | null): any
    getNickname(compPointer: CompRef, context?: null | CompRef): string | undefined
    getComponentsInContainer(containerPointer: Pointer, context?: CompRef | null): Pointer[]
}

export type NicknamesExtensionAPI = ExtensionAPI & {
    nicknames: NicknamesAPI
}
export const NICKNAMES_EVENTS = {
    WIX_CODE: {
        SET_NICKNAME_AFTER: 'SET_NICKNAME_AFTER'
    }
}
const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const getKeyFilterName = (page: string, nickname: string) => `${page}__${nickname}`

    const createExtensionAPI = ({pointers, extensionAPI, dal, eventEmitter}: CreateExtArgs): NicknamesExtensionAPI => {
        const getNicknameByConnectionPointer = (
            connectionPtr: Pointer,
            pagePointer: Pointer,
            compPointer: Pointer,
            context?: CompRef | null
        ) => {
            const {connections} = extensionAPI as ConnectionsAPI
            const connectionItems = connections.getResolvedConnectionsByDataItem(
                connectionPtr.id,
                pagePointer,
                compPointer.type
            )

            if (connectionItems) {
                return getNicknameFromConnectionList(connectionItems, context)
            }
        }

        const getRefComponentNicknameFromOverrides = (
            compPointer: Pointer,
            compNickname: string,
            context?: CompRef | null
        ) => {
            const pagePointer = pointers.structure.getPageOfComponent(compPointer)
            return _(compPointer)
                .thru(pointers.referredStructure.getConnectionOverrides)
                .mapKeys((connectionPtr: Pointer) => extractBaseComponentId(connectionPtr))
                .mapValues((connectionPtr: Pointer) =>
                    getNicknameByConnectionPointer(connectionPtr, pagePointer, compPointer, context)
                )
                .assign({[compPointer.id]: compNickname})
                .pickBy()
                .value()
        }

        function getComponentNickname(compPointer: Pointer, context?: CompRef | null) {
            const {connections} = extensionAPI as ConnectionsAPI
            const compConnections = connections.getResolved(compPointer)

            const nickname = getNicknameFromConnectionList(compConnections, context)
            const compData = dal.get(compPointer)
            const componentType = _.get(compData, ['componentType'])

            if (componentType === COMP_TYPES.REF_TYPE) {
                return getRefComponentNicknameFromOverrides(compPointer, nickname!, context)
            }
            return nickname ? {[compPointer.id]: nickname} : {}
        }

        const getNicknames = (comps: Pointer[], context?: CompRef | null) => {
            return _(comps)
                .map(c => getComponentNickname(c, context))
                .reduce(_.assign)
        }

        const hasInvalidCharacters = (nickname: string) => {
            const validName = /^[a-zA-Z0-9\-]+$/
            return !validName.test(nickname)
        }

        const nicknameKeyExists = (keyFilterName: string, compPointerToExclude: Pointer | undefined): boolean => {
            const filter = dal.queryFilterGetters.nicknames(keyFilterName)
            const filterResult = Object.values(dal.query('connections', filter))
            if (!compPointerToExclude || filterResult.length === 0) {
                return filterResult.length > 0
            }
            const {dataModel} = extensionAPI as DataModelExtensionAPI

            const connectionToExclude =
                compPointerToExclude.type === 'DESKTOP'
                    ? dataModel.components.getItem(compPointerToExclude, 'connections')
                    : dataModel.components.getItem({...compPointerToExclude, type: 'DESKTOP'}, 'connections') ??
                      dataModel.components.getItem(compPointerToExclude, 'connections')
            return !connectionToExclude || filterResult.filter(item => connectionToExclude.id !== item.id).length > 0
        }

        const hasComponentWithThatNicknameWithIndex = (
            containingPagePointer: Pointer,
            searchedNickname: string,
            compPointerToExclude: Pointer | undefined
        ) => {
            const actualCompPointerToExclude = compPointerToExclude
                ? getPointer(getRepeaterTemplateId(compPointerToExclude.id), compPointerToExclude.type)
                : compPointerToExclude

            if (containingPagePointer.id === MASTER_PAGE_ID) {
                return nicknameKeyExists(
                    getKeyFilterName(ALL_PAGES_FILTER, searchedNickname),
                    actualCompPointerToExclude
                )
            }

            return (
                nicknameKeyExists(getKeyFilterName(MASTER_PAGE_ID, searchedNickname), actualCompPointerToExclude) ||
                nicknameKeyExists(
                    getKeyFilterName(containingPagePointer.id, searchedNickname),
                    actualCompPointerToExclude
                )
            )
        }

        const hasComponentWithThatNickname = (
            containingPagePointer: Pointer,
            searchedNickname: string,
            compPointerToExclude?: Pointer | undefined
        ) => {
            if (!searchedNickname) {
                return false
            }
            return hasComponentWithThatNicknameWithIndex(containingPagePointer, searchedNickname, compPointerToExclude)
        }

        const validateNickname = (compPointer: Pointer, nickname: string, hasComponentWithThatNicknameFn?: any) => {
            if (_.isEmpty(nickname)) {
                return VALIDATIONS.TOO_SHORT
            }

            if (nickname.length > MAX_NICKNAME_LENGTH) {
                return VALIDATIONS.TOO_LONG
            }

            const _hasComponentWithThatNicknameFn = hasComponentWithThatNicknameFn || hasComponentWithThatNickname
            const componentPagePointer = pointers.structure.getPageOfComponent(compPointer)

            if (_hasComponentWithThatNicknameFn(componentPagePointer, nickname, compPointer)) {
                return VALIDATIONS.ALREADY_EXISTS
            }

            if (hasInvalidCharacters(nickname)) {
                return VALIDATIONS.INVALID_NAME
            }

            return VALIDATIONS.VALID
        }
        const createConnectionItemByContext = (context: null | CompRef, nickname: string): any => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const {connections} = extensionAPI as ConnectionsAPI
            return context
                ? connections.createConnectionItem(
                      nickname,
                      dataModel.components.getItem(context, DATA_TYPES.data).id,
                      true
                  )
                : connections.createWixCodeConnectionItem(nickname)
        }
        const setNickname = (
            compPointer: CompRef,
            nickname: string,
            context = (extensionAPI as NicknameContextExtensionAPI).nicknameContext.getNicknameContext(compPointer)
        ) => {
            if (validateNickname(compPointer, nickname) !== VALIDATIONS.VALID) {
                throw new ReportableError({
                    errorType: 'invalidNickname',
                    message: 'The new nickname you provided is invalid'
                })
            }
            const {connections} = extensionAPI as ConnectionsAPI
            let currentConnections = connections.getResolved(compPointer) as IConnectionItem[]
            const connection = findConnectionByContext(currentConnections, context)
            if (connection) {
                connection.role = nickname
            } else {
                const newConnectionItem = createConnectionItemByContext(context, nickname)
                currentConnections = currentConnections!.concat([newConnectionItem])
            }

            connections.updateConnectionsItem(compPointer, currentConnections!)
            eventEmitter.emit(NICKNAMES_EVENTS.WIX_CODE.SET_NICKNAME_AFTER, compPointer, nickname)
        }

        const createNicknameOverride = (refRootId: string, pageId: string, nickname: string) => {
            const {connections} = extensionAPI as ConnectionsAPI
            const {refOverrides} = extensionAPI as RefOverridesExtensionAPI

            const connectionItemId = refOverrides.getOverrideId(refRootId, VIEWER_DATA_TYPES.connections)
            let currentConnections = deepClone(connections.getConnectionsByDataItem(connectionItemId, pageId))
            const connection = getWixCodeConnectionItem(currentConnections)
            if (connection) {
                connection.role = nickname
            } else {
                const newConnectionItem = connections.createWixCodeConnectionItem(nickname)
                currentConnections = currentConnections!.concat([newConnectionItem])
            }
            connections.updateConnectionsItemByDataItem(connectionItemId, pageId, currentConnections!)
        }

        const getComponentsInContainer = (containerPointer: Pointer, context?: CompRef | null) => {
            const componentsInContainer =
                pointers.structure.getChildrenRecursivelyRightLeftRootIncludingRoot(containerPointer)
            return context ? _.reject(componentsInContainer, context) : componentsInContainer
        }

        const getLowestAvailableSuffix = (suffixes: number[]) => {
            const usedSuffixes = new Set(suffixes)
            let nextSuffix = 1

            while (usedSuffixes.has(nextSuffix)) {
                nextSuffix++
            }

            return nextSuffix
        }

        const getNextSuffixIndex = (compNickname: string, compsNicknames: UsedNickNames) => {
            const regex = new RegExp(`${compNickname}(\\d+)`) //will match the number in the end of the nickname
            const suffixes = _(compsNicknames)
                .values()
                .map(nickname => {
                    const match = regex.exec(nickname)
                    return match ? parseInt(match[1], 10) : null
                })
                .compact()
                .value()

            const maxSuffix = suffixes.length > 0 ? Math.max(...suffixes) : 0
            const nextSuffix = maxSuffix + 1

            if (nextSuffix >= Number.MAX_SAFE_INTEGER) {
                return getLowestAvailableSuffix(suffixes)
            }

            const nextNickname = `${compNickname}${nextSuffix}`
            if (hasInvalidCharacters(nextNickname)) {
                return getLowestAvailableSuffix(suffixes)
            }

            return nextSuffix
        }

        const shouldSetNickname = (compPointer: CompRef, context?: null | CompRef) => {
            const {componentsMetadata} = extensionAPI as ComponentsMetadataAPI
            return (
                !pointers.structure.isMasterPage(compPointer) &&
                !getComponentNickname(compPointer, context)[compPointer.id] &&
                componentsMetadata.shouldAutoSetNickname(compPointer)
            )
        }

        const generateNicknamesForComponentImpl = (compPointer: CompRef, usedNickNames: UsedNickNames) => {
            const {componentsMetadata} = extensionAPI as ComponentsMetadataAPI
            const defaultCompNickname = componentsMetadata.getDefaultNickname(compPointer)
            const suffixToUse = getNextSuffixIndex(defaultCompNickname, usedNickNames)
            const newNickname = defaultCompNickname + suffixToUse
            setNickname(compPointer, newNickname)
        }

        function usedNickNamesFromAllPages(page: PageAPI, viewMode: 'MOBILE' | 'DESKTOP') {
            const allPagesIds = page.getAllPagesIds(true) // Include master page
            let allCompsInAllPages: Pointer[] = []

            for (const pageId of allPagesIds) {
                const currentPagePointer = pointers.structure.getPage(pageId, viewMode)
                const compsInPage = getComponentsInContainer(currentPagePointer)
                allCompsInAllPages = allCompsInAllPages.concat(compsInPage)
            }

            return getNicknames(allCompsInAllPages)
        }

        const getUsedNicknames = (pagePointer: Pointer, viewMode: PossibleViewModes = VIEW_MODES.DESKTOP) => {
            const masterPagePointer = pointers.structure.getPage(MASTER_PAGE_ID, viewMode)
            if (experimentInstance.isOpen('dm_supportAddComponentV2InPopulate')) {
                const {page} = extensionAPI as PageExtensionAPI

                //DMS flow
                if (page.isPartiallyLoaded()) {
                    if (pagePointer.id === MASTER_PAGE_ID) {
                        throw Error('Cannot add currently add to master page')
                    }
                }
                return usedNickNamesFromAllPages(page, viewMode)
            }
            if (pagePointer.id === MASTER_PAGE_ID) {
                throw Error('Cannot add currently add to master page')
            }

            const allCompsInContext = getComponentsInContainer(pagePointer).concat(
                getComponentsInContainer(masterPagePointer)
            )
            return getNicknames(allCompsInContext)
        }

        const generateConnectionOverrideForRefComponentRoot = (
            compPointer: Pointer,
            pagePointer: Pointer,
            rootCompId: string,
            rootComponentType: string
        ) => {
            const usedNickNames = getUsedNicknames(pagePointer)
            const inflatedRefId = createInflatedRefId(compPointer.id, rootCompId)
            const defaultCompNickname = getComponentNicknameByType({dal, pointers}, rootComponentType) || 'widget'
            const suffixToUse = getNextSuffixIndex(defaultCompNickname, usedNickNames)
            const newNickname = defaultCompNickname + suffixToUse
            createNicknameOverride(inflatedRefId, pagePointer.id, newNickname)
        }
        const generateNicknamesForComponent = (
            compPointer: CompRef,
            pagePointer: Pointer,
            viewMode: PossibleViewModes = VIEW_MODES.DESKTOP
        ) => {
            const usedNicknames = getUsedNicknames(pagePointer, viewMode)
            generateNicknamesForComponentImpl(compPointer, usedNicknames)
        }
        const setNicknamesForComponentsWithoutNickname = (
            comps: CompRef[],
            defaultCompNickname: string,
            usedNickNames: UsedNickNames,
            context: CompRef | null,
            pagePointer: CompRef
        ): CompRef[] => {
            let maxSuffixOfDefaultNickname = getNextSuffixIndex(defaultCompNickname, usedNickNames)

            return _(comps)
                .filter(compPointer => shouldSetNickname(compPointer, context))
                .map(function (comp) {
                    while (
                        hasComponentWithThatNickname(
                            pagePointer,
                            `${defaultCompNickname}${maxSuffixOfDefaultNickname}`,
                            comp
                        )
                    ) {
                        maxSuffixOfDefaultNickname++
                    }
                    const newNickname = `${defaultCompNickname}${maxSuffixOfDefaultNickname}`

                    setNickname(comp, newNickname, context)
                    return comp
                })
                .value()
        }

        const getNickname = (
            compPointer: CompRef,
            context: null | CompRef = (extensionAPI as NicknameContextExtensionAPI).nicknameContext.getNicknameContext(
                compPointer
            )
        ) => {
            const {connections} = extensionAPI as ConnectionsAPI
            const compConnections = connections.get(compPointer)
            return getNicknameFromConnectionList(compConnections, context)
        }

        return {
            nicknames: {
                hasComponentWithThatNickname,
                shouldSetNickname,
                getComponentNickname,
                getNextSuffixIndex,
                generateNicknamesForComponent,
                setNickname,
                validateNickname,
                generateConnectionOverrideForRefComponentRoot,
                getNicknames,
                getNickname,
                getComponentsInContainer,
                setNicknamesForComponentsWithoutNickname
            }
        }
    }

    const initialize = async (extArgs: InitializeExtArgs) => {
        const {eventEmitter, EVENTS, extensionAPI, dal, pointers} = extArgs
        const addNicknameIfNeeded = (compPointer: CompRef, pagePointer: Pointer) => {
            const {wixCode} = extensionAPI as WixCodeExtensionAPI
            const {nicknames} = extensionAPI as NicknamesExtensionAPI
            if (wixCode.isProvisioned() && nicknames.shouldSetNickname(compPointer)) {
                nicknames.generateNicknamesForComponent(compPointer, pagePointer)
            }
        }

        const generateConnectionOverrideForRefComponent = (compPointer: CompRef, pagePointer: Pointer) => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const {nicknames} = extensionAPI as NicknamesExtensionAPI
            const componentData = dataModel.components.getItem(compPointer, 'data')
            const {appDefinitionId, widgetId} = componentData
            const removeWidgetMetaData = dal.get(
                pointers.remoteStructureMetaData.getRemoteStructureWidgetMetaData(appDefinitionId, widgetId)
            )
            if (!removeWidgetMetaData) {
                throw new Error('Need to load remote widget meta data for nickname creation')
            }
            const {rootId} = removeWidgetMetaData
            const rootComponentType = removeWidgetMetaData.rootType
            nicknames.generateConnectionOverrideForRefComponentRoot(compPointer, pagePointer, rootId, rootComponentType)
        }
        eventEmitter.on(EVENTS.COMPONENTS.AFTER_ADD_FROM_EXT, (componentPointer: CompRef, pagePointer: Pointer) => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI

            const compData = dataModel.components.getItem(componentPointer, VIEWER_DATA_TYPES.data)
            const componentType = _.get(compData, ['type'])
            if (componentType === COMPS_DATA_TYPE.BLOCK_WIDGET) {
                return generateConnectionOverrideForRefComponent(componentPointer, pagePointer)
            }
            addNicknameIfNeeded(componentPointer, pagePointer)
        })
    }

    const createFilters = () => {
        return {
            nicknames: (namespace: Namespace, value: DalValue) => {
                const result: string[] = []
                if (namespace !== 'connections' && value.type !== 'ConnectionList') {
                    return result
                }

                value.items
                    ?.filter(
                        (item: DalValue) => item?.type === 'WixCodeConnectionItem' || item?.type === 'ConnectionItem'
                    )
                    .forEach((connection: DalValue) => {
                        const nickname = connection?.role
                        if (nickname) {
                            result.push(getKeyFilterName(value.metaData.pageId, nickname))
                            result.push(getKeyFilterName(ALL_PAGES_FILTER, nickname))
                        }
                    })

                return result
            }
        }
    }

    return {
        EVENTS: NICKNAMES_EVENTS,
        name: 'nicknames',
        dependencies: new Set(['dataModel', 'components']),
        createExtensionAPI,
        initialize,
        createFilters
    }
}

export {createExtension}
